import React, { useState } from 'react';
import { formatStepError } from './stepError';
import type { DoctorPractice, DoctorPracticeTiming } from '../../../types/doctorOnboarding';

type BreakEntry = { break_start_time: string; break_end_time: string };

function formatTimingLine(t: DoctorPracticeTiming): string {
  const day = t.day_of_week.charAt(0).toUpperCase() + t.day_of_week.slice(1);
  const start = t.start_time?.slice(0, 5) || '–';
  const end = t.end_time?.slice(0, 5) || '–';
  const breakStr =
    t.breaks && t.breaks.length > 0
      ? ` (Breaks: ${t.breaks
          .map((b) => {
            const s = (b.break_start_time || '').slice(0, 5);
            const e = (b.break_end_time || '').slice(0, 5);
            const [sh, sm] = s.split(':').map(Number);
            const [eh, em] = e.split(':').map(Number);
            const durationMin = eh * 60 + em - (sh * 60 + sm);
            return `${s}–${e} (${durationMin} min)`;
          })
          .join(', ')})`
      : '';
  return `${day} ${start}–${end}${breakStr}`;
}

type Props = {
  doctorId: number | null;
  entries: DoctorPractice[];
  practiceTypes: Array<{ id: number; name: string; code: string; is_active: boolean }>;
  onAdd: (
    body: Omit<DoctorPractice, 'id' | 'created_at' | 'updated_at'>
  ) => Promise<DoctorPractice>;
  onDelete: (id: number) => Promise<void>;
  onAddTiming: (
    practiceId: number,
    timing: Omit<DoctorPracticeTiming, 'id' | 'practice_id' | 'created_at' | 'updated_at'>
  ) => Promise<void>;
  onNext: () => void;
  onBack: () => void;
};

const DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) =>
  i.toString().padStart(2, '0')
);
const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) =>
  i.toString().padStart(2, '0')
);

function timeToParts(time: string): { hour: string; minute: string } {
  const [h = '09', m = '00'] = time.split(':');
  return {
    hour: h.padStart(2, '0'),
    minute: m.padStart(2, '0'),
  };
}

function partsToTime(hour: string, minute: string): string {
  return `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
}

export default function PracticesStep({
  doctorId,
  entries,
  practiceTypes,
  onAdd,
  onDelete,
  onAddTiming,
  onNext,
  onBack,
}: Props) {
  const [practiceType, setPracticeType] = useState('clinic');
  const [practiceName, setPracticeName] = useState('');
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postal, setPostal] = useState('');
  const [country, setCountry] = useState('India');
  const [contact, setContact] = useState('');
  const [websiteLink, setWebsiteLink] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);

  const [loading, setLoading] = useState(false);
  const [timingModalPractice, setTimingModalPractice] = useState<DoctorPractice | null>(null);

  type DayConfig = { enabled: boolean; start: string; end: string };
  const [timingDays, setTimingDays] = useState<Record<string, DayConfig>>({
    monday: { enabled: false, start: '09:00', end: '17:00' },
    tuesday: { enabled: false, start: '09:00', end: '17:00' },
    wednesday: { enabled: false, start: '09:00', end: '17:00' },
    thursday: { enabled: false, start: '09:00', end: '17:00' },
    friday: { enabled: false, start: '09:00', end: '17:00' },
    saturday: { enabled: false, start: '09:00', end: '17:00' },
    sunday: { enabled: false, start: '09:00', end: '17:00' },
  });
  const [timingSlotDuration, setTimingSlotDuration] = useState(15);
  const [timingBreaksByDay, setTimingBreaksByDay] = useState<Record<string, BreakEntry[]>>({});
  const [timingSaving, setTimingSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFieldErrors, setShowFieldErrors] = useState(false);

  const hasPractice = entries.length > 0;
  const hasTiming = entries.some((p) => (p.timings || []).length > 0);
  const canContinue = hasPractice && hasTiming;

  const trimmedName = practiceName.trim();
  const trimmedAddress1 = address1.trim();
  const trimmedCity = city.trim();
  const trimmedState = state.trim();
  const contactDigits = contact.replace(/\D/g, '');
  const postalDigits = postal.replace(/\D/g, '');

  const websiteTrimmed = websiteLink.trim();
  const emailTrimmed = contactEmail.trim();
  const urlRegex = /^https?:\/\/.+/i;
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const websiteValid = !websiteTrimmed || (websiteTrimmed.length <= 500 && urlRegex.test(websiteTrimmed));
  const emailValid = !emailTrimmed || (emailTrimmed.length <= 255 && emailRegex.test(emailTrimmed));

  const nameValid = trimmedName.length >= 3 && trimmedName.length <= 100;
  const address1Valid = trimmedAddress1.length >= 5 && trimmedAddress1.length <= 200;
  const cityValid = trimmedCity.length >= 2 && trimmedCity.length <= 100;
  const stateValid = trimmedState.length >= 2 && trimmedState.length <= 100;
  const postalValid = postalDigits.length >= 4 && postalDigits.length <= 10;
  const contactValid = contactDigits.length >= 10 && contactDigits.length <= 15;

  const nameError = !trimmedName ? 'Practice name is required' : trimmedName.length < 3 ? 'Practice name must be at least 3 characters' : trimmedName.length > 100 ? 'Practice name must not exceed 100 characters' : null;
  const address1Error = !trimmedAddress1 ? 'Address line 1 is required' : trimmedAddress1.length < 5 ? 'Address must be at least 5 characters' : trimmedAddress1.length > 200 ? 'Address must not exceed 200 characters' : null;
  const cityError = !trimmedCity ? 'City is required' : trimmedCity.length < 2 ? 'City must be at least 2 characters' : trimmedCity.length > 100 ? 'City must not exceed 100 characters' : null;
  const stateError = !trimmedState ? 'State is required' : trimmedState.length < 2 ? 'State must be at least 2 characters' : trimmedState.length > 100 ? 'State must not exceed 100 characters' : null;
  const postalError = !postalDigits ? 'Postal code is required' : postalDigits.length < 4 ? 'Postal code must be at least 4 digits' : postalDigits.length > 10 ? 'Postal code must not exceed 10 digits' : null;
  const contactError = !contactDigits ? 'Contact number is required' : contactDigits.length < 10 ? 'Contact number must be at least 10 digits' : contactDigits.length > 15 ? 'Contact number must not exceed 15 digits' : null;
  const websiteError = !websiteValid && websiteTrimmed ? 'Enter a valid URL (e.g. https://example.com)' : null;
  const emailError = !emailValid && emailTrimmed ? 'Enter a valid email address' : null;

  const allFieldsValid =
    !!doctorId &&
    nameValid &&
    address1Valid &&
    cityValid &&
    stateValid &&
    postalValid &&
    contactValid &&
    websiteValid &&
    emailValid;

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowFieldErrors(true);
    if (!allFieldsValid || !doctorId) return;
    setLoading(true);
    setError(null);
    try {
      const created = await onAdd({
        doctor_id: doctorId,
        practice_type: practiceType,
        practice_name: trimmedName,
        address_line1: trimmedAddress1,
        address_line2: address2.trim() || undefined,
        city: trimmedCity,
        state: trimmedState,
        postal_code: postalDigits,
        country: country.trim() || 'India',
        contact_number: contactDigits,
        website_link: websiteLink.trim() || undefined,
        contact_email: contactEmail.trim() || undefined,
        is_primary: isPrimary,
      } as DoctorPractice);
      setPracticeType('clinic');
      setPracticeName('');
      setAddress1('');
      setAddress2('');
      setCity('');
      setState('');
      setPostal('');
      setCountry('India');
      setContact('');
      setWebsiteLink('');
      setContactEmail('');
      setIsPrimary(false);
      setShowFieldErrors(false);
      openTimingModal(created);
    } catch (e: any) {
      setError(formatStepError(e, 'Failed to add practice'));
    } finally {
      setLoading(false);
    }
  };

  const openTimingModal = (practice: DoctorPractice) => {
    setTimingModalPractice(practice);
    setError(null);
    const existing = practice.timings || [];
    const dayMap: Record<string, DayConfig> = { monday: { enabled: false, start: '09:00', end: '17:00' }, tuesday: { enabled: false, start: '09:00', end: '17:00' }, wednesday: { enabled: false, start: '09:00', end: '17:00' }, thursday: { enabled: false, start: '09:00', end: '17:00' }, friday: { enabled: false, start: '09:00', end: '17:00' }, saturday: { enabled: false, start: '09:00', end: '17:00' }, sunday: { enabled: false, start: '09:00', end: '17:00' } };
    const breaksByDay: Record<string, BreakEntry[]> = {};
    DAYS.forEach((day) => {
      const t = existing.find((x) => x.day_of_week.toLowerCase() === day);
      if (t) {
        dayMap[day] = { enabled: true, start: (t.start_time || '').slice(0, 5) || '09:00', end: (t.end_time || '').slice(0, 5) || '17:00' };
        const rawBreaks = t.breaks ?? [];
        breaksByDay[day] = rawBreaks.map((b) => ({
          break_start_time: (b.break_start_time || '').slice(0, 5) || '12:00',
          break_end_time: (b.break_end_time || '').slice(0, 5) || '13:00',
        }));
      }
    });
    setTimingDays(dayMap);
    setTimingSlotDuration(existing[0]?.slot_duration ?? 15);
    setTimingBreaksByDay(breaksByDay);
  };

  const closeTimingModal = () => {
    setTimingModalPractice(null);
    setTimingBreaksByDay({});
  };

  const getBreaksForDay = (day: string): BreakEntry[] => timingBreaksByDay[day] ?? [];
  const addBreakForDay = (day: string) => {
    setTimingBreaksByDay((prev) => ({
      ...prev,
      [day]: [...(prev[day] ?? []), { break_start_time: '12:00', break_end_time: '13:00' }],
    }));
  };
  const removeBreakForDay = (day: string, index: number) => {
    setTimingBreaksByDay((prev) => {
      const list = (prev[day] ?? []).slice();
      list.splice(index, 1);
      return { ...prev, [day]: list };
    });
  };
  const updateBreakForDay = (day: string, index: number, field: 'break_start_time' | 'break_end_time', value: string) => {
    setTimingBreaksByDay((prev) => {
      const list = (prev[day] ?? []).slice();
      if (list[index]) list[index] = { ...list[index], [field]: value.slice(0, 5) };
      return { ...prev, [day]: list };
    });
  };

  const copyTimingToAllDays = (sourceDay: string) => {
    const config = timingDays[sourceDay];
    if (!config?.enabled) return;
    const start = config.start;
    const end = config.end;
    const sourceBreaks = (timingBreaksByDay[sourceDay] ?? []).map((b) => ({ ...b }));
    setTimingDays((prev) => {
      const next = { ...prev };
      DAYS.forEach((d) => {
        if (prev[d].enabled) next[d] = { ...prev[d], start, end };
      });
      return next;
    });
    setTimingBreaksByDay((prev) => {
      const next = { ...prev };
      DAYS.forEach((d) => {
        if (timingDays[d]?.enabled) next[d] = sourceBreaks.length ? sourceBreaks.map((b) => ({ ...b })) : [];
      });
      return next;
    });
  };

  const hasAtLeastOneDaySelected = DAYS.some((day) => timingDays[day].enabled);
  const saveTimingModal = async () => {
    const practice = timingModalPractice;
    if (!practice?.id) return;
    if (!hasAtLeastOneDaySelected) {
      setError('Select at least one day.');
      return;
    }
    const existingDays = new Set((practice.timings || []).map((t) => t.day_of_week.toLowerCase()));
    const slotDuration = Math.min(120, Math.max(1, Number(timingSlotDuration) || 15));
    setError(null);
    setTimingSaving(true);
    try {
      for (const day of DAYS) {
        const config = timingDays[day];
        if (!config.enabled || existingDays.has(day)) continue;
        await onAddTiming(practice.id, {
          day_of_week: day,
          is_available: true,
          start_time: config.start,
          end_time: config.end,
          slot_duration: slotDuration,
          breaks: getBreaksForDay(day),
        });
      }
      closeTimingModal();
    } catch (e: any) {
      setError(formatStepError(e, 'Failed to add timing'));
    } finally {
      setTimingSaving(false);
    }
  };

  const updateTimingDay = (day: string, patch: Partial<DayConfig>) => {
    setTimingDays((prev) => ({ ...prev, [day]: { ...prev[day], ...patch } }));
  };

  return (
    <div>
      <div className="onboarding-section">
        <h3>Practice Information</h3>
        <form onSubmit={handleAdd}>
          <div className="form-group">
            <label>Practice Type</label>
            <select value={practiceType} onChange={(e) => setPracticeType(e.target.value)}>
              {practiceTypes.filter((p) => p.is_active).map((p) => (
                <option key={p.id} value={p.code || p.name.toLowerCase()}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Practice Name <span className="required">*</span></label>
            <input value={practiceName} onChange={(e) => setPracticeName(e.target.value)} />
            {showFieldErrors && nameError && <span className="field-hint">{nameError}</span>}
          </div>
          <div className="form-group">
            <label>Address Line 1 <span className="required">*</span></label>
            <input value={address1} onChange={(e) => setAddress1(e.target.value)} />
            {showFieldErrors && address1Error && <span className="field-hint">{address1Error}</span>}
          </div>
          <div className="form-group">
            <label>Address Line 2</label>
            <input value={address2} onChange={(e) => setAddress2(e.target.value)} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>City <span className="required">*</span></label>
              <input value={city} onChange={(e) => setCity(e.target.value)} />
              {showFieldErrors && cityError && <span className="field-hint">{cityError}</span>}
            </div>
            <div className="form-group">
              <label>State <span className="required">*</span></label>
              <input value={state} onChange={(e) => setState(e.target.value)} />
              {showFieldErrors && stateError && <span className="field-hint">{stateError}</span>}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Postal Code <span className="required">*</span></label>
              <input value={postal} onChange={(e) => setPostal(e.target.value.replace(/\D/g, ''))} />
              {showFieldErrors && postalError && <span className="field-hint">{postalError}</span>}
            </div>
            <div className="form-group">
              <label>Country</label>
              <input value={country} onChange={(e) => setCountry(e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label>Contact Number <span className="required">*</span></label>
            <input value={contact} onChange={(e) => setContact(e.target.value.replace(/\D/g, ''))} />
            {showFieldErrors && contactError && <span className="field-hint">{contactError}</span>}
          </div>
          <div className="form-group">
            <label>Website link</label>
            <input type="url" value={websiteLink} onChange={(e) => setWebsiteLink(e.target.value)} placeholder="https://..." />
            {showFieldErrors && websiteError && <span className="field-hint">{websiteError}</span>}
          </div>
          <div className="form-group">
            <label>Contact email address</label>
            <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="practice@example.com" />
            {showFieldErrors && emailError && <span className="field-hint">{emailError}</span>}
          </div>
          {error && <div className="onboarding-error">{error}</div>}
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Saving...' : 'Add Practice'}
          </button>
        </form>
      </div>

      <div className="onboarding-section">
        <h3>Your Practices</h3>
        {entries.length === 0 && <p className="onboarding-step-label">Add a practice first.</p>}
        {!hasTiming && hasPractice && (
          <div className="onboarding-error timing-not-provided-message" role="alert">
            Timings are required. Add at least one timing to each practice (use &quot;Add Timings&quot; or &quot;Edit Timings&quot;) before you can continue.
          </div>
        )}
        {entries.map((p) => (
          <div key={p.id} className="onboarding-list-item practice-list-item">
            <div className="practice-list-item-details">
              <div className="practice-list-item-header">
                <strong>{p.practice_name}</strong>
                <span className="practice-list-item-type">
                  {practiceTypes.find((t) => (t.code || t.name.toLowerCase()) === p.practice_type)?.name ?? p.practice_type}
                </span>
              </div>
              <div className="practice-list-item-address">
                {p.address_line1}
                {p.address_line2 && `, ${p.address_line2}`}
              </div>
              <div className="practice-list-item-location">
                {[p.city, p.state, p.postal_code].filter(Boolean).join(', ')}
                {p.country && `, ${p.country}`}
              </div>
              {p.contact_number && (
                <div className="practice-list-item-contact">
                  Contact: {p.contact_number}
                </div>
              )}
              {(p.timings?.length ?? 0) > 0 && (
                <div className="practice-list-item-timings">
                  <span className="onboarding-step-label" style={{ display: 'block', marginBottom: 4 }}>
                    {(p.timings || []).length} timing(s)
                  </span>
                  <ul className="practice-timings-list">
                    {(p.timings || [])
                      .sort((a, b) => DAYS.indexOf(a.day_of_week.toLowerCase()) - DAYS.indexOf(b.day_of_week.toLowerCase()))
                      .map((t) => (
                        <li key={t.id}>{formatTimingLine(t)}</li>
                      ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="onboarding-inline-actions">
              <button type="button" className="btn-secondary" onClick={() => openTimingModal(p)}>
                {(p.timings?.length ?? 0) > 0 ? 'Edit Timings' : 'Add Timings'}
              </button>
              <button type="button" className="btn-secondary" onClick={() => onDelete(p.id)}>
                Remove Practice
              </button>
            </div>
          </div>
        ))}
      </div>

      {timingModalPractice && (
        <div className="onboarding-modal-overlay" onClick={closeTimingModal} role="presentation">
          <div className="onboarding-modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Configure Timings — {timingModalPractice.practice_name}</h3>
            {!hasAtLeastOneDaySelected && (
              <div className="timing-required-message" role="alert">
                Timings are required. Select at least one day and set start/end times before saving.
              </div>
            )}
            <p className="onboarding-step-label" style={{ marginBottom: 16 }}>
              Select at least one day and set start/end times. Slot duration applies to all.
            </p>
            <div className="timing-days-list">
              {DAYS.map((day) => {
                const config = timingDays[day];
                const label = day.charAt(0).toUpperCase() + day.slice(1);
                const startParts = timeToParts(config.start);
                const endParts = timeToParts(config.end);
                return (
                  <div key={day} className="timing-day-row">
                    <label>
                      <input
                        type="checkbox"
                        checked={config.enabled}
                        onChange={(e) => updateTimingDay(day, { enabled: e.target.checked })}
                      />
                      {label}
                    </label>
                    {config.enabled && (
                      <>
                        <div className="timing-time-dropdowns">
                          <div className="timing-time-group">
                            <span className="timing-time-label">Start</span>
                            <select
                              value={startParts.hour}
                              onChange={(e) =>
                                updateTimingDay(day, {
                                  start: partsToTime(e.target.value, startParts.minute),
                                })
                              }
                              aria-label={`${label} start hour`}
                              style={{ backgroundColor: '#fff' }}
                            >
                              {HOUR_OPTIONS.map((h) => (
                                <option key={h} value={h}>
                                  {h}
                                </option>
                              ))}
                            </select>
                            <span className="timing-time-sep">:</span>
                            <select
                              value={startParts.minute}
                              onChange={(e) =>
                                updateTimingDay(day, {
                                  start: partsToTime(startParts.hour, e.target.value),
                                })
                              }
                              aria-label={`${label} start minute`}
                              style={{ backgroundColor: '#fff' }}
                            >
                              {MINUTE_OPTIONS.map((m) => (
                                <option key={m} value={m}>
                                  {m}
                                </option>
                              ))}
                            </select>
                          </div>
                          <span className="timing-time-dash">–</span>
                          <div className="timing-time-group">
                            <span className="timing-time-label">End</span>
                            <select
                              value={endParts.hour}
                              onChange={(e) =>
                                updateTimingDay(day, {
                                  end: partsToTime(e.target.value, endParts.minute),
                                })
                              }
                              aria-label={`${label} end hour`}
                              style={{ backgroundColor: '#fff' }}
                            >
                              {HOUR_OPTIONS.map((h) => (
                                <option key={h} value={h}>
                                  {h}
                                </option>
                              ))}
                            </select>
                            <span className="timing-time-sep">:</span>
                            <select
                              value={endParts.minute}
                              onChange={(e) =>
                                updateTimingDay(day, {
                                  end: partsToTime(endParts.hour, e.target.value),
                                })
                              }
                              aria-label={`${label} end minute`}
                              style={{ backgroundColor: '#fff' }}
                            >
                              {MINUTE_OPTIONS.map((m) => (
                                <option key={m} value={m}>
                                  {m}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <button type="button" className="btn-secondary" style={{ marginLeft: 8 }} onClick={() => copyTimingToAllDays(day)} title="Copy this day's times and breaks to other enabled days">
                          Copy to all
                        </button>
                        <div className="timing-breaks-section" style={{ marginTop: 8, marginLeft: 24 }}>
                          <div style={{ fontSize: 12, color: 'var(--text-secondary, #666)', marginBottom: 4 }}>Breaks</div>
                          {getBreaksForDay(day).map((br, idx) => (
                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                              <select
                                value={timeToParts(br.break_start_time).hour}
                                onChange={(e) => updateBreakForDay(day, idx, 'break_start_time', partsToTime(e.target.value, timeToParts(br.break_start_time).minute))}
                                style={{ backgroundColor: '#fff' }}
                              >
                                {HOUR_OPTIONS.map((h) => (
                                  <option key={h} value={h}>{h}</option>
                                ))}
                              </select>
                              <span>:</span>
                              <select
                                value={timeToParts(br.break_start_time).minute}
                                onChange={(e) => updateBreakForDay(day, idx, 'break_start_time', partsToTime(timeToParts(br.break_start_time).hour, e.target.value))}
                                style={{ backgroundColor: '#fff' }}
                              >
                                {MINUTE_OPTIONS.map((m) => (
                                  <option key={m} value={m}>{m}</option>
                                ))}
                              </select>
                              <span>–</span>
                              <select
                                value={timeToParts(br.break_end_time).hour}
                                onChange={(e) => updateBreakForDay(day, idx, 'break_end_time', partsToTime(e.target.value, timeToParts(br.break_end_time).minute))}
                                style={{ backgroundColor: '#fff' }}
                              >
                                {HOUR_OPTIONS.map((h) => (
                                  <option key={h} value={h}>{h}</option>
                                ))}
                              </select>
                              <span>:</span>
                              <select
                                value={timeToParts(br.break_end_time).minute}
                                onChange={(e) => updateBreakForDay(day, idx, 'break_end_time', partsToTime(timeToParts(br.break_end_time).hour, e.target.value))}
                                style={{ backgroundColor: '#fff' }}
                              >
                                {MINUTE_OPTIONS.map((m) => (
                                  <option key={m} value={m}>{m}</option>
                                ))}
                              </select>
                              <button type="button" className="btn-secondary" style={{ marginLeft: 4 }} onClick={() => removeBreakForDay(day, idx)}>Remove</button>
                            </div>
                          ))}
                          <button type="button" className="btn-secondary" onClick={() => addBreakForDay(day)}>Add break</button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="form-group" style={{ marginTop: 16 }}>
              <label>Slot Duration (min)</label>
              <input
                type="number"
                min={5}
                max={120}
                value={timingSlotDuration}
                onChange={(e) => setTimingSlotDuration(Number(e.target.value))}
                style={{ backgroundColor: '#fff' }}
              />
            </div>
            {error && <div className="onboarding-error">{error}</div>}
            <div className="onboarding-modal-actions">
              <button type="button" className="btn-secondary" onClick={closeTimingModal} disabled={timingSaving}>
                Cancel
              </button>
              <button type="button" className="btn-primary" onClick={saveTimingModal} disabled={timingSaving || !hasAtLeastOneDaySelected}>
                {timingSaving ? 'Saving...' : 'Save Timings'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="step-actions">
        <button type="button" className="btn-secondary" onClick={onBack}>
          Back
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={onNext}
          disabled={!canContinue}
        >
          Continue
        </button>
      </div>
    </div>
  );
}
